import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  BookOpen, Check, ChevronLeft, Heart, Loader2, Search, Skull, Sparkles,
  Target, Zap, Feather, Rocket, Map, Lightbulb, PenTool, Smile, FileSearch,
  Layers, ChefHat
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Card, CardContent } from "../ui/card";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../../lib/api";

const ICON_MAP = {
  target: Target,
  heart: Heart,
  search: Search,
  rocket: Rocket,
  sparkles: Sparkles,
  skull: Skull,
  feather: Feather,
  "book-open": BookOpen,
  landmark: Map,
  zap: Zap,
  "file-search": FileSearch,
  lightbulb: Lightbulb,
  smile: Smile,
  "pen-tool": PenTool,
  "chef-hat": Layers,
};

const COLOR_MAP = {
  red: { bg: "from-red-950/60 to-red-900/20", border: "border-red-500/40 hover:border-red-400/70", icon: "bg-red-500/20 text-red-400", badge: "bg-red-500", text: "text-red-200/60" },
  pink: { bg: "from-pink-950/60 to-pink-900/20", border: "border-pink-500/40 hover:border-pink-400/70", icon: "bg-pink-500/20 text-pink-400", badge: "bg-pink-500", text: "text-pink-200/60" },
  cyan: { bg: "from-slate-800/50 to-slate-800/30", border: "border-cyan-500/30 hover:border-cyan-500/40", icon: "bg-cyan-500/20 text-cyan-400", badge: "bg-cyan-500", text: "text-cyan-200/60" },
  blue: { bg: "from-blue-950/60 to-blue-900/20", border: "border-blue-500/40 hover:border-blue-400/70", icon: "bg-blue-500/20 text-blue-400", badge: "bg-blue-500", text: "text-blue-200/60" },
  purple: { bg: "from-purple-950/60 to-purple-900/20", border: "border-purple-500/40 hover:border-purple-400/70", icon: "bg-purple-500/20 text-purple-400", badge: "bg-purple-500", text: "text-purple-200/60" },
  slate: { bg: "from-slate-950/60 to-slate-900/20", border: "border-slate-500/40 hover:border-slate-400/70", icon: "bg-slate-500/20 text-slate-400", badge: "bg-slate-600", text: "text-slate-200/60" },
  emerald: { bg: "from-emerald-950/60 to-emerald-900/20", border: "border-emerald-500/40 hover:border-emerald-400/70", icon: "bg-emerald-500/20 text-emerald-400", badge: "bg-emerald-500", text: "text-emerald-200/60" },
  orange: { bg: "from-slate-800/50 to-orange-900/20", border: "border-orange-500/40 hover:border-orange-400/70", icon: "bg-orange-500/20 text-orange-400", badge: "bg-orange-500", text: "text-orange-200/60" },
  violet: { bg: "from-violet-950/60 to-violet-900/20", border: "border-violet-500/40 hover:border-violet-400/70", icon: "bg-violet-500/20 text-violet-400", badge: "bg-violet-500", text: "text-violet-200/60" },
  sky: { bg: "from-sky-950/60 to-sky-900/20", border: "border-sky-500/40 hover:border-sky-400/70", icon: "bg-sky-500/20 text-sky-400", badge: "bg-sky-500", text: "text-sky-200/60" },
  rose: { bg: "from-rose-950/60 to-rose-900/20", border: "border-rose-500/40 hover:border-rose-400/70", icon: "bg-rose-500/20 text-rose-400", badge: "bg-rose-500", text: "text-rose-200/60" },
  yellow: { bg: "from-yellow-950/60 to-yellow-900/20", border: "border-yellow-500/40 hover:border-yellow-400/70", icon: "bg-cyan-500/20 text-yellow-400", badge: "bg-yellow-600", text: "text-yellow-200/60" },
};

const GenreTemplateMode = ({ onBack, userId, onCreated }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [title, setTitle] = useState("");
  const [selectedPrompt, setSelectedPrompt] = useState(null);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const res = await axios.get(`${API}/genre-templates`);
        setTemplates(res.data.templates || []);
      } catch (err) {
        toast.error("Failed to load genre templates");
      } finally {
        setLoading(false);
      }
    };
    fetchTemplates();
  }, []);

  const filtered = templates.filter(t =>
    t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleCreate = async () => {
    if (!selected) return;
    if (!title.trim()) {
      toast.error("Give your project a title");
      return;
    }
    setCreating(true);
    try {
      const template = selected;
      const projectData = {
        title: title.trim(),
        genre: template.genre,
        user_id: userId,
        story_bible: {
          genre_tone: template.story_bible_defaults?.genre_tone || "",
          genre_tone_lock: template.story_bible_defaults?.genre_tone || "",
          world_rules: template.story_bible_defaults?.world_rules || [],
          exclusions: template.story_bible_defaults?.exclusions || [],
          narration_style: template.narration_defaults || {},
        },
        chapter_outline: [],
        characters: [],
        content: "",
        word_count: 0,
        privacy_no_training: false,
        genre_template_id: template.id,
        genre_template_meta: {
          chapter_structure: template.chapter_structure,
          starter_prompt_used: selectedPrompt,
        },
      };

      const res = await axios.post(`${API}/projects`, projectData);
      toast.success(`${template.name} project created!`);
      if (onCreated) onCreated(res.data);
    } catch (err) {
      toast.error("Failed to create project: " + (err.response?.data?.detail || err.message));
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-teal-400" />
      </div>
    );
  }

  // Template detail view
  if (selected) {
    const t = selected;
    const colors = COLOR_MAP[t.color] || COLOR_MAP.emerald;
    const IconComp = ICON_MAP[t.icon] || BookOpen;
    const struct = t.chapter_structure || {};

    return (
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        className="space-y-4 py-2 overflow-y-auto max-h-[calc(90vh-120px)]"
      >
        <Button variant="ghost" size="sm" onClick={() => setSelected(null)} className="text-muted-foreground" data-testid="genre-detail-back-btn">
          <ChevronLeft className="w-4 h-4 mr-1" /> Back to genres
        </Button>

        <div className="flex items-center gap-3 mb-3">
          <div className={`w-12 h-12 rounded-xl ${colors.icon} flex items-center justify-center`}>
            <IconComp className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-foreground">{t.name}</h2>
            <p className="text-sm text-muted-foreground">{t.description}</p>
          </div>
        </div>

        {/* Project Title */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Project Title</label>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={`My ${t.name} Novel...`}
            className="bg-slate-900/50 border-slate-700"
            data-testid="genre-project-title-input"
          />
        </div>

        {/* What you get */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Check className="w-4 h-4 text-emerald-400" /> What this blueprint includes
          </h3>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-700/50">
              <p className="font-medium text-foreground mb-1">Tone & Style</p>
              <p className="text-muted-foreground">{t.story_bible_defaults?.genre_tone?.slice(0, 80)}...</p>
            </div>
            <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-700/50">
              <p className="font-medium text-foreground mb-1">Structure</p>
              <p className="text-muted-foreground">{struct.suggested_count || '?'} chapters, ~{struct.avg_words || '?'} words each</p>
            </div>
            <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-700/50 col-span-2">
              <p className="font-medium text-foreground mb-1">POV & Narration</p>
              <p className="text-muted-foreground">{(t.narration_defaults?.pov || 'third_person_limited').replace(/_/g, ' ')}</p>
            </div>
          </div>
        </div>

        {/* Story Starters */}
        {t.starter_prompts?.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-foreground">Story Starters (optional — pick one to inspire your story)</h3>
            <div className="space-y-2">
              {t.starter_prompts.map((prompt, i) => (
                <button
                  key={i}
                  onClick={() => setSelectedPrompt(selectedPrompt === prompt ? null : prompt)}
                  className={`w-full text-left text-xs p-3 rounded-lg border transition-all ${
                    selectedPrompt === prompt
                      ? 'border-teal-400 bg-teal-950/40 text-teal-100'
                      : 'border-slate-700/50 bg-slate-900/30 text-muted-foreground hover:border-slate-600'
                  }`}
                  data-testid={`genre-starter-prompt-${i}`}
                >
                  {selectedPrompt === prompt && <Check className="w-3 h-3 inline mr-1 text-teal-400" />}
                  {prompt}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* World Rules */}
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-foreground">Story Bible Rules</h3>
          <div className="space-y-1">
            {t.story_bible_defaults?.world_rules?.map((rule, i) => (
              <div key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
                <Check className="w-3 h-3 mt-0.5 text-emerald-400 shrink-0" />
                <span>{rule}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Structure Notes */}
        {struct.structure_notes && (
          <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-700/50">
            <h3 className="text-sm font-semibold text-foreground mb-1">Structure Guide</h3>
            <p className="text-xs text-muted-foreground leading-relaxed">{struct.structure_notes}</p>
          </div>
        )}

        <Button
          onClick={handleCreate}
          disabled={creating || !title.trim()}
          className="w-full bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 text-white py-3"
          data-testid="genre-create-project-btn"
        >
          {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Zap className="w-4 h-4 mr-2" />}
          {creating ? 'Creating...' : `Create ${t.name} Project`}
        </Button>
      </motion.div>
    );
  }

  // Genre grid view
  return (
    <div className="space-y-3 py-2 overflow-y-auto max-h-[calc(90vh-120px)]">
      <Button variant="ghost" size="sm" onClick={onBack} className="text-muted-foreground" data-testid="genre-back-btn">
        <ChevronLeft className="w-4 h-4 mr-1" /> Back
      </Button>

      <div className="text-center mb-3">
        <h2 className="text-lg font-bold text-foreground">Genre Blueprints</h2>
        <p className="text-sm text-muted-foreground">Pick a genre and get a pre-built Story Bible, structure, and writing rules</p>
      </div>

      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search genres..."
          className="pl-9 bg-slate-900/50 border-slate-700"
          data-testid="genre-search-input"
        />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 pb-4">
        <AnimatePresence>
          {filtered.map((t, idx) => {
            const colors = COLOR_MAP[t.color] || COLOR_MAP.emerald;
            const IconComp = ICON_MAP[t.icon] || BookOpen;
            return (
              <motion.div
                key={t.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.03 }}
              >
                <Card
                  className={`cursor-pointer border ${colors.border} bg-gradient-to-br ${colors.bg} hover:shadow-lg transition-all duration-300 h-full`}
                  onClick={() => { setSelected(t); setTitle(""); setSelectedPrompt(null); }}
                  data-testid={`genre-card-${t.id}`}
                >
                  <CardContent className="py-4 px-3 text-center">
                    <div className={`w-10 h-10 mx-auto rounded-xl ${colors.icon} flex items-center justify-center mb-2`}>
                      <IconComp className="w-5 h-5" />
                    </div>
                    <h3 className="font-bold text-sm text-foreground">{t.name}</h3>
                    <p className={`text-[11px] ${colors.text} mt-1 line-clamp-2`}>{t.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      {filtered.length === 0 && (
        <p className="text-center text-muted-foreground text-sm py-8">No genres match "{searchQuery}"</p>
      )}
    </div>
  );
};

export default GenreTemplateMode;
